import { getIn, type FormikProps } from "formik";

/**
 * Formik error state in the shape the DS fields want.
 *
 * Extracted from `admin/cards/CardConfigModules`, which had it private, when
 * the contact forms became the second and third caller.
 *
 * Generic over the form's values because the three forms here hold different
 * ones; the field is addressed by path, so nested fields work too.
 */

/** Whether any of the fields is both touched and in error. */
export const isTouchedAndError = <Values>(
  formik: FormikProps<Values>,
  fields: string | string[],
): boolean => {
  const fieldList = Array.isArray(fields) ? fields : [fields];
  return fieldList.some(
    (field) => getIn(formik.touched, field) && getIn(formik.errors, field),
  );
};

/**
 * The message for the first touched field in error, or `undefined`.
 *
 * `undefined` and never `false`: the DS resolves a field's message as
 * `errorText ?? helperText`, and `??` does not fall through on `false`, so a
 * boolean here silently replaces the helper text with nothing and shows no
 * error either. That shipped on three fields before OUT-1404 found it.
 */
export const fieldErrorText = <Values>(
  formik: FormikProps<Values>,
  fields: string | string[],
): string | undefined => {
  const fieldList = Array.isArray(fields) ? fields : [fields];
  for (const field of fieldList) {
    const touched = getIn(formik.touched, field);
    const error = getIn(formik.errors, field);
    if (!touched || !error) continue;
    if (typeof error === "string") return error;
    // Formik nests errors for object-shaped fields.
    // Not null: the guard above already skipped every falsy error.
    if (typeof error === "object") {
      const firstError = Object.values(error).find(
        (value) => typeof value === "string",
      );
      if (firstError) return firstError as string;
    }
  }
  return undefined;
};
